import * as React from "react";
import {
  AutoSizer as _AutoSizer,
  List as _List,
  InfiniteLoader as _InfiniteLoader,
  IndexRange,
  InfiniteLoaderProps,
  AutoSizerProps,
  ListProps,
} from "react-virtualized";
import { EmailValue } from "../../Components/EmailValue";
import { EmailImportance, EmailListItemAbstractAddress } from "../../DefaultLayout/EmailListItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { dayjs } from "Config/dayjs";
import classnames from "classnames";
import { OutlookCheckbox } from "./OutlookCheckbox";
import i18n, { I18N_NS } from "Config/i18n";
import classNames from "classnames";
import { Skeleton, Space } from "antd";
import { isEmpty } from "lodash";
const InfiniteLoader = _InfiniteLoader as unknown as React.FC<InfiniteLoaderProps>;
const AutoSizer = _AutoSizer as unknown as React.FC<AutoSizerProps>;
const List = _List as unknown as React.FC<ListProps>;

interface OutlookLayoutSiderProps {
  emails: API_MailResponse[];
  loading?: boolean;
  onEmailClick: (email: API_MailResponse) => void;
  onEmailCheck: (email: API_MailResponse) => void;
  selectedEmails: { [emailId: string]: API_MailResponse };
  currentlySelectedEmailId: string;
  totalEmails: number;
  loadMore: (params: { offset: number; limit: number }) => Promise<void>;
}

export class OutlookLayoutSider extends React.Component<OutlookLayoutSiderProps, { scrollToIndex: number }> {
  readonly state: { scrollToIndex: number } = {
    scrollToIndex: -1, // TODO: not implemented yet. maybe will not ever be
  };

  handleClick = (e: React.MouseEvent) => {
    const node = e.currentTarget;
    const emailId = node instanceof HTMLElement ? node.dataset.id : "";
    const emailData = this.props.emails.find((item) => item.id === emailId);
    this.props.onEmailClick(emailData);
  };

  handleCheck = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const node = e.currentTarget;
    const emailId = node instanceof HTMLElement ? node.dataset.id : "";
    const emailData = this.props.emails.find((item) => item.id === emailId);
    this.props.onEmailCheck(emailData);
  };

  handleLoadMore = ({ startIndex, stopIndex }: IndexRange) => {
    return this.props.loadMore({ offset: startIndex, limit: stopIndex - startIndex });
  };

  renderEmailAddress = (email: API_MailResponse, receiversKey: "to" | "from") => {
    if (receiversKey === "from") {
      return [<EmailValue key="1" uid={email[receiversKey]} backupName={email.correspondents[email[receiversKey]]} />];
    }
    let content = email[receiversKey].map((item) => (
      <EmailValue key={item} uid={item} backupName={email.correspondents[item]} />
    ));
    if (!isEmpty((email as API_MailResponse).abstractRecipients?.to)) {
      content = [
        <EmailListItemAbstractAddress
          key="1"
          abstractRecipients={email.abstractRecipients}
          correspondents={email.correspondents}
        />,
      ];
    }
    return content;
  };

  render() {
    const { emails, selectedEmails, currentlySelectedEmailId, totalEmails } = this.props;
    return (
      <div className="email-sider-list">
        {this.props.loading && (
          <div className="email-sider-loader">{i18n.t("Loading...", { ns: I18N_NS.PROJECT }) as string}</div>
        )}

        <AutoSizer>
          {({ width, height }) => (
            <InfiniteLoader
              isRowLoaded={({ index }) => !!emails[index]}
              loadMoreRows={this.handleLoadMore}
              rowCount={totalEmails}
              minimumBatchSize={30}
              threshold={15}
            >
              {({ onRowsRendered }) => (
                <List
                  width={width}
                  height={height}
                  rowCount={emails.length}
                  scrollToIndex={this.state.scrollToIndex}
                  onRowsRendered={onRowsRendered}
                  rowHeight={70}
                  rowRenderer={({ index, key, style }) => {
                    const email = emails[index];
                    if (!email)
                      return (
                        <div className="email-sider-item" key={key} style={style}>
                          <Space orientation="vertical" size={10}>
                            <Skeleton.Node active style={{ height: 40, marginTop: 15 }} />
                          </Space>
                        </div>
                      );
                    const receiversKey: keyof Pick<API_MailResponse, "to" | "from"> =
                      email.type === "SENT" || email.type === "DRAFT" ? "to" : "from";
                    const receivers = this.renderEmailAddress(email, receiversKey);
                    return (
                      <div
                        key={key}
                        className={classnames("email-sider-item", {
                          selected: selectedEmails[email.id],
                          active: email.id === currentlySelectedEmailId,
                          unread: email.unread,
                        })}
                        style={style}
                        data-id={emails[index].id}
                        onClick={this.handleClick}
                      >
                        <div className="email-item-checkbox">
                          <OutlookCheckbox
                            dataId={emails[index].id}
                            onClick={this.handleCheck}
                            checked={!!selectedEmails[email.id]}
                          />
                        </div>
                        <div className="email-item-header">
                          <div className={classNames("email-item-title", { "color-grey": !email.subject })}>
                            {email.subject || `[${i18n.t("No subject", { ns: I18N_NS.PROJECT })}]`}
                          </div>
                          <div className="email-item-info">
                            <EmailImportance type={email.importance} />
                            {email.hasAttachments && <FontAwesomeIcon icon={["fal", "paperclip"]} />}
                          </div>
                        </div>
                        <div className="email-item-data">
                          <div className={classNames("email-item-emails", { "color-grey": !receivers.length })}>
                            {receivers.length > 0 ? receivers : `[${i18n.t("No recipients", { ns: I18N_NS.PROJECT })}]`}
                          </div>
                          <div className="email-item-time">{dayjs(+email.timestamp).format("lll")}</div>
                        </div>
                        {email.excerpt ? (
                          <div className="email-item-excerpt" dangerouslySetInnerHTML={{ __html: email.excerpt }} />
                        ) : (
                          <div className="email-item-excerpt color-grey">{`[${i18n.t("No email text", {
                            ns: I18N_NS.PROJECT,
                          })}]`}</div>
                        )}
                      </div>
                    );
                  }}
                />
              )}
            </InfiniteLoader>
          )}
        </AutoSizer>
      </div>
    );
  }
}
